'use client';

import { useSession } from 'next-auth/react';
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useWebSocketManager } from '@/hooks/useWebSocketManager';

interface WebSocketContextType {
  isConnected: boolean;
  sendMessage: (message: any) => void;
}

const WebSocketContext = createContext<WebSocketContextType>({
  isConnected: false,
  sendMessage: () => {},
});

export const useWebSocketContext = () => {
  try {
    const context = useContext(WebSocketContext);
    if (context === undefined) {
      console.warn('useWebSocket called outside of WebSocketProvider');
      return {
        isConnected: false,
        sendMessage: () => {},
      };
    }
    return context;
  } catch (error) {
    console.error('Error in useWebSocketContext:', error);
    return {
      isConnected: false,
      sendMessage: () => {},
    };
  }
};

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  
  // Don't try to access session during loading
  if (status === 'loading') {
    return (
      <WebSocketContext.Provider value={{ isConnected: false, sendMessage: () => {} }}>
        {children}
      </WebSocketContext.Provider>
    );
  }

  const { subscribe, emit, connect, disconnect } = useWebSocketManager(
    session?.user?.token || null,
  );

  useEffect(() => {
    if (session?.user?.token) {
      connect();
    } else {
      disconnect();
    }
  }, [session?.user?.token, connect, disconnect]);

  const value = useMemo<WebSocketContextType>(
    () => ({
      isConnected: !!session?.user?.token,
      sendMessage: (message: any) => {
        if (session?.user?.token) {
          emit(message);
        } else {
          console.warn('Cannot send message: Not connected');
        }
      },
    }),
    [session?.user?.token, emit],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
